/**
 * Offline Storage Service
 * 
 * Servicio para almacenamiento offline de datos usando Capacitor Preferences.
 * REQUERIDO para modo offline - diferenciador clave vs webapp.
 * 
 * @module native/offline-storage
 */

import { Preferences } from '@capacitor/preferences';
import { Logger } from '@/core/utils';
import { networkService } from './network.service';

const logger = Logger.getLogger('OfflineStorageService');

export interface OfflineQueueItem {
  id: string;
  action: 'create' | 'update' | 'delete';
  collection: string;
  data: unknown;
  timestamp: number;
  retries: number;
}

export interface CachedPractice {
  id: string;
  title: string;
  description: string;
  duration: number;
  content: string;
  cachedAt: number;
}

class OfflineStorageService {
  private static instance: OfflineStorageService;
  private readonly SYNC_QUEUE_KEY = 'appmor_sync_queue';
  private readonly CACHED_PRACTICES_KEY = 'appmor_cached_practices';
  private readonly USER_PREFERENCES_KEY = 'appmor_user_preferences';
  private readonly LAST_SYNC_KEY = 'appmor_last_sync';

  private constructor() {
    // Escuchar restauración de conexión para sincronizar
    window.addEventListener('connection-restored', () => {
      this.processSyncQueue();
    });
  }

  static getInstance(): OfflineStorageService {
    if (!OfflineStorageService.instance) {
      OfflineStorageService.instance = new OfflineStorageService();
    }
    return OfflineStorageService.instance;
  }

  // ============================================
  // Almacenamiento genérico
  // ============================================

  /**
   * Guarda un valor
   */
  async set(key: string, value: unknown): Promise<void> {
    try {
      await Preferences.set({
        key,
        value: JSON.stringify(value),
      });
    } catch (error) {
      logger.error('Error guardando en storage', { key, error });
      throw error;
    }
  }

  /**
   * Obtiene un valor
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const { value } = await Preferences.get({ key });
      if (value === null) return null;
      return JSON.parse(value) as T;
    } catch (error) {
      logger.error('Error leyendo de storage', { key, error });
      return null;
    }
  }

  /**
   * Elimina un valor
   */
  async remove(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch (error) {
      logger.error('Error eliminando de storage', { key, error });
    }
  }

  /**
   * Limpia todo el storage
   */
  async clear(): Promise<void> {
    try {
      await Preferences.clear();
      logger.info('Storage limpiado completamente');
    } catch (error) {
      logger.error('Error limpiando storage', { error });
    }
  }

  // ============================================
  // Cache de prácticas (para modo offline)
  // ============================================

  /**
   * Cachea prácticas para uso offline
   */
  async cachePractices(practices: CachedPractice[]): Promise<void> {
    try {
      const cachedData = practices.map(p => ({
        ...p,
        cachedAt: Date.now(),
      }));
      await this.set(this.CACHED_PRACTICES_KEY, cachedData);
      logger.info('Prácticas cacheadas', { count: practices.length });
    } catch (error) {
      logger.error('Error cacheando prácticas', { error });
    }
  }

  /**
   * Obtiene prácticas cacheadas
   */
  async getCachedPractices(): Promise<CachedPractice[]> {
    try {
      const practices = await this.get<CachedPractice[]>(this.CACHED_PRACTICES_KEY);
      return practices || [];
    } catch (error) {
      logger.error('Error obteniendo prácticas cacheadas', { error });
      return [];
    }
  }

  /**
   * Obtiene una práctica cacheada por ID
   */
  async getCachedPractice(practiceId: string): Promise<CachedPractice | null> {
    const practices = await this.getCachedPractices();
    return practices.find(p => p.id === practiceId) || null;
  }

  /**
   * Verifica si hay prácticas cacheadas recientes (menos de 24h)
   */
  async hasFreshCache(): Promise<boolean> {
    const practices = await this.getCachedPractices();
    if (practices.length === 0) return false;

    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
    return practices.some(p => p.cachedAt > twentyFourHoursAgo);
  }

  // ============================================
  // Cola de sincronización
  // ============================================

  /**
   * Añade una acción a la cola de sincronización
   */
  async addToSyncQueue(item: Omit<OfflineQueueItem, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    try {
      const queue = await this.getSyncQueue();
      const newItem: OfflineQueueItem = {
        ...item,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        retries: 0,
      };
      queue.push(newItem);
      await this.set(this.SYNC_QUEUE_KEY, queue);
      logger.info('Añadido a cola de sincronización', { action: item.action, collection: item.collection });
    } catch (error) {
      logger.error('Error añadiendo a cola de sync', { error });
    }
  }

  /**
   * Obtiene la cola de sincronización
   */
  async getSyncQueue(): Promise<OfflineQueueItem[]> {
    try {
      const queue = await this.get<OfflineQueueItem[]>(this.SYNC_QUEUE_KEY);
      return queue || [];
    } catch (error) {
      logger.error('Error obteniendo cola de sync', { error });
      return [];
    }
  }

  /**
   * Procesa la cola de sincronización cuando hay conexión
   */
  async processSyncQueue(): Promise<void> {
    if (networkService.isOffline()) {
      logger.info('Sin conexión, sincronización pospuesta');
      return;
    }

    const queue = await this.getSyncQueue();
    if (queue.length === 0) {
      logger.info('Cola de sincronización vacía');
      return;
    }

    logger.info('Procesando cola de sincronización', { count: queue.length });
    
    const failedItems: OfflineQueueItem[] = [];
    
    for (const item of queue) {
      try {
        await this.processSyncItem(item);
        logger.info('Item sincronizado', { id: item.id, action: item.action });
      } catch (error) {
        logger.error('Error sincronizando item', { id: item.id, error });
        
        // Reintentar hasta 3 veces
        if (item.retries < 3) {
          failedItems.push({ ...item, retries: item.retries + 1 });
        } else {
          logger.warn('Item descartado después de 3 reintentos', { id: item.id });
        }
      }
    }

    // Guardar items fallidos para reintento
    await this.set(this.SYNC_QUEUE_KEY, failedItems);
    
    // Actualizar timestamp de última sincronización
    await this.set(this.LAST_SYNC_KEY, Date.now());
    
    logger.info('Sincronización completada', { 
      processed: queue.length - failedItems.length,
      pending: failedItems.length 
    });
  }

  /**
   * Procesa un item individual de la cola
   */
  private async processSyncItem(item: OfflineQueueItem): Promise<void> {
    // Este método debería llamar al servicio correspondiente
    // Por ahora es un placeholder que será implementado según el tipo
    
    // Emitir evento para que los servicios escuchen
    window.dispatchEvent(new CustomEvent('sync-item', {
      detail: item
    }));
    
    // Simular procesamiento exitoso
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Obtiene la cantidad de items pendientes de sincronización
   */
  async getPendingSyncCount(): Promise<number> {
    const queue = await this.getSyncQueue();
    return queue.length;
  }

  /**
   * Limpia la cola de sincronización
   */
  async clearSyncQueue(): Promise<void> {
    await this.remove(this.SYNC_QUEUE_KEY);
    logger.info('Cola de sincronización limpiada');
  }

  // ============================================
  // Preferencias de usuario
  // ============================================

  /**
   * Guarda preferencias de usuario
   */
  async saveUserPreferences(preferences: Record<string, unknown>): Promise<void> {
    await this.set(this.USER_PREFERENCES_KEY, preferences);
  }

  /**
   * Obtiene preferencias de usuario
   */
  async getUserPreferences(): Promise<Record<string, unknown>> {
    const prefs = await this.get<Record<string, unknown>>(this.USER_PREFERENCES_KEY);
    return prefs || {};
  }

  /**
   * Obtiene una preferencia específica
   */
  async getUserPreference<T>(key: string, defaultValue: T): Promise<T> {
    const prefs = await this.getUserPreferences();
    return (prefs[key] as T) ?? defaultValue;
  }

  /**
   * Guarda una preferencia específica
   */
  async setUserPreference(key: string, value: unknown): Promise<void> {
    const prefs = await this.getUserPreferences();
    prefs[key] = value;
    await this.saveUserPreferences(prefs);
  }

  // ============================================
  // Estado de sincronización
  // ============================================

  /**
   * Obtiene el timestamp de la última sincronización
   */
  async getLastSyncTime(): Promise<number | null> {
    return this.get<number>(this.LAST_SYNC_KEY);
  }

  /**
   * Verifica si necesita sincronización (más de 1 hora sin sync)
   */
  async needsSync(): Promise<boolean> {
    const lastSync = await this.getLastSyncTime();
    if (!lastSync) return true;
    
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    return lastSync < oneHourAgo;
  }
}

export const offlineStorageService = OfflineStorageService.getInstance();










